import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  EllipsisVerticalIcon,
  MinusIcon,
} from '@heroicons/react/24/outline'
import { useEffect, useRef, useState } from 'react'
import type { ReactElement, ReactNode } from 'react'
import type { HabitWithLog } from '../interfaces'
import { formatDeeplinkDisplay, openDeeplink } from '../utils/deeplink'
import {
  getHabitNameStyle,
  getHabitToggleStyle,
} from '../utils/habitCardStyles'
import { getHabitHighlight, getWeeklyNoticeInfo } from '../utils/habitHighlight'
import { SubHabitRow } from './SubHabitRow'

export interface HabitCardProps {
  habit: HabitWithLog
  isReadOnlyDate?: boolean
  isFutureDate?: boolean
  selectedDate: Date
  dayStartTime: string
  dragHandle?: ReactNode
  onToggle: (habit: HabitWithLog) => Promise<void>
  onEdit: (habit: HabitWithLog) => void
  onSkip: (habit: HabitWithLog) => Promise<void>
  onTogglePinned: (habit: HabitWithLog) => Promise<void>
  onSubHabitToggle?: (subHabit: HabitWithLog, parentId: string) => Promise<void>
}

function getToggleLabel(habit: HabitWithLog, isReadOnly: boolean): string {
  if (isReadOnly) return 'Cannot mark future habits'
  const isWeeklyDone =
    habit.isWeeklyTargetMet && !habit.logId && !habit.isSkippedThisPeriod
  if (isWeeklyDone) return 'Log extra day'
  if (habit.isCompletedThisPeriod) return 'Mark incomplete'
  if (habit.isSkippedThisPeriod) return 'Mark complete (currently skipped)'
  return 'Mark complete'
}

function getFrequencyLabel(habit: HabitWithLog): string {
  if (habit.frequency_type === 'daily') return 'Daily'
  if (habit.frequency_type === 'weekly') return 'Weekly'
  if (habit.frequency_type === 'weekly_days') {
    if (!habit.weekly_days || habit.weekly_days.length === 0)
      return 'Specific Days'
    const dayNames: Record<string, string> = {
      mon: 'Mon',
      tue: 'Tue',
      wed: 'Wed',
      thu: 'Thu',
      fri: 'Fri',
      sat: 'Sat',
      sun: 'Sun',
    }
    return habit.weekly_days.map((d) => dayNames[d] ?? d).join(', ')
  }
  return `${habit.frequency_target ?? 1}×/week`
}

function getFrequencyClass(habit: HabitWithLog): string {
  if (habit.frequency_type === 'daily') return 'bg-blue-100 text-blue-700'
  if (habit.frequency_type === 'weekly') return 'bg-purple-100 text-purple-700'
  if (habit.frequency_type === 'weekly_days')
    return 'bg-indigo-100 text-indigo-700'
  return 'bg-teal-100 text-teal-700'
}

function getCategoryClass(category: string): string {
  if (category === 'Health') return 'bg-emerald-100 text-emerald-700'
  if (category === 'Learning') return 'bg-sky-100 text-sky-700'
  if (category === 'Work') return 'bg-slate-100 text-slate-700'
  if (category === 'Personal') return 'bg-rose-100 text-rose-700'
  return 'bg-gray-100 text-gray-600'
}

export function HabitCard({
  habit,
  isReadOnlyDate,
  isFutureDate,
  selectedDate,
  dayStartTime,
  dragHandle,
  onToggle,
  onEdit,
  onSkip,
  onTogglePinned,
  onSubHabitToggle,
}: HabitCardProps): ReactElement {
  const [menuOpen, setMenuOpen] = useState(false)
  const [toggling, setToggling] = useState(false)
  const [subHabitsExpanded, setSubHabitsExpanded] = useState(true)
  const menuRef = useRef<HTMLDivElement>(null)

  const isReadOnly = isReadOnlyDate ?? isFutureDate ?? false

  const hasSubHabits =
    habit.subHabits !== undefined && habit.subHabits.length > 0

  useEffect(() => {
    function handleOutside(event: MouseEvent): void {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false)
      }
    }

    if (menuOpen) document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [menuOpen])

  async function handleToggle(): Promise<void> {
    // Parent habits with sub-habits are completed via sub-habits — no direct toggle
    if (hasSubHabits) return
    const isCompleting = !habit.isCompletedThisPeriod
    if (isCompleting && habit.deeplink_url) {
      openDeeplink(habit.deeplink_url)
    }
    setToggling(true)
    try {
      await onToggle(habit)
    } finally {
      setToggling(false)
    }
  }

  const toggleDisabled = toggling || isReadOnly || hasSubHabits
  const cardOpacity = toggling
    ? 'pointer-events-none opacity-50'
    : habit.isCompletedThisPeriod
      ? 'opacity-60'
      : habit.isSkippedThisPeriod
        ? 'opacity-50'
        : 'opacity-100'
  const toggleStyle = getHabitToggleStyle({
    isCompleted: habit.isCompletedThisPeriod,
    isSkipped: habit.isSkippedThisPeriod,
    isReadOnlyDate: isReadOnly,
  })
  const nameStyle = getHabitNameStyle(habit, isReadOnly)

  const { borderClass: importantClass, highlightReasonEmoji } =
    getHabitHighlight(habit, selectedDate, dayStartTime)
  const weeklyNotice = getWeeklyNoticeInfo(habit, selectedDate, dayStartTime)

  return (
    <div
      className={`flex flex-col rounded-2xl border bg-white shadow-sm transition-opacity ${importantClass} ${cardOpacity}`}
    >
      {/* Main habit row */}
      <div className="flex items-center gap-3 px-4 py-4">
        {dragHandle}

        {/* Toggle / progress indicator */}
        {hasSubHabits ? (
          <div className="flex shrink-0 flex-col items-center gap-0.5">
            <span
              className={`flex h-7 w-7 items-center justify-center rounded-full border-2 font-bold text-xs ${toggleStyle}`}
            >
              {habit.isCompletedThisPeriod ? (
                <CheckIcon className="h-4 w-4" />
              ) : (
                <span>
                  {habit.weeklyCompletions}/{habit.frequency_target ?? 1}
                </span>
              )}
            </span>
          </div>
        ) : (
          <button
            type="button"
            onClick={handleToggle}
            disabled={toggleDisabled}
            aria-label={getToggleLabel(habit, isReadOnly)}
            className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${toggleStyle}`}
          >
            {toggling ? (
              <ArrowPathIcon className="h-4 w-4 animate-spin text-gray-400" />
            ) : habit.isCompletedThisPeriod && !isReadOnly ? (
              <CheckIcon className="h-4 w-4" />
            ) : habit.isSkippedThisPeriod && !isReadOnly ? (
              <MinusIcon className="h-4 w-4" />
            ) : null}
          </button>
        )}

        {/* Name + badges — clicking opens sub-habits if parent, otherwise toggles */}
        <button
          type="button"
          onClick={
            hasSubHabits ? () => setSubHabitsExpanded((e) => !e) : handleToggle
          }
          disabled={!hasSubHabits && toggleDisabled}
          className="flex flex-1 flex-col items-start gap-0.5 text-left"
        >
          <span
            className={`flex items-center font-medium text-base ${nameStyle}`}
          >
            {highlightReasonEmoji && (
              <span className="mr-1.5 inline-flex" title="Important today">
                {highlightReasonEmoji}
              </span>
            )}
            {habit.name}
          </span>
          {habit.description && (
            <span className="line-clamp-1 text-gray-500 text-sm">
              {habit.description}
            </span>
          )}
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
            {hasSubHabits ? (
              <span className="rounded-full bg-violet-100 px-2 py-0.5 font-medium text-violet-700 text-xs">
                {habit.weeklyCompletions}/{habit.frequency_target ?? 1} this
                week
              </span>
            ) : (
              <span
                className={`rounded-full px-2 py-0.5 font-medium text-xs ${getFrequencyClass(
                  habit,
                )}`}
              >
                {getFrequencyLabel(habit)}
              </span>
            )}
            <span
              className={`rounded-full px-2 py-0.5 font-medium text-xs ${getCategoryClass(
                habit.category,
              )}`}
            >
              {habit.category}
            </span>
            {habit.pinned && (
              <span className="rounded-full bg-indigo-100 px-2 py-0.5 font-medium text-indigo-700 text-xs">
                📌 Pinned
              </span>
            )}
            {habit.isSkippedThisPeriod && !isReadOnly && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-700 text-xs">
                ⏭️ Skipped
              </span>
            )}
            {!hasSubHabits && habit.frequency_type === 'x_per_week' ? (
              <span
                className={`rounded-full px-2 py-0.5 font-medium text-xs ${
                  habit.weeklyCompletions >= (habit.frequency_target ?? 1)
                    ? 'bg-green-100 text-green-700'
                    : 'bg-orange-100 text-orange-700'
                }`}
              >
                {habit.weeklyCompletions}/{habit.frequency_target ?? 1} this
                week
                {habit.currentStreak > 0 && (
                  <span className="opacity-70">
                    {' '}
                    · 🔥{habit.currentStreak}w
                  </span>
                )}
              </span>
            ) : (
              !hasSubHabits &&
              habit.currentStreak > 0 && (
                <span className="rounded-full bg-orange-100 px-2 py-0.5 font-medium text-orange-700 text-xs">
                  🔥 {habit.currentStreak}
                  {habit.bestStreak > habit.currentStreak && (
                    <span className="text-orange-400">
                      {' '}
                      | best {habit.bestStreak}
                    </span>
                  )}
                </span>
              )
            )}
            {weeklyNotice.badgeText && (
              <span
                className={`rounded-full px-2 py-0.5 font-medium text-xs ${weeklyNotice.badgeClass}`}
              >
                {weeklyNotice.badgeText}
              </span>
            )}
            {habit.deeplink_url && (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 font-medium text-blue-700 text-xs"
                title={habit.deeplink_url}
              >
                <span>📱</span>
                <span>{formatDeeplinkDisplay(habit.deeplink_url)}</span>
              </span>
            )}
          </div>
        </button>

        {/* Expand/collapse chevron for parent habits */}
        {hasSubHabits && (
          <button
            type="button"
            onClick={() => setSubHabitsExpanded((e) => !e)}
            aria-label={
              subHabitsExpanded ? 'Collapse sub-habits' : 'Expand sub-habits'
            }
            className="rounded-full p-1.5 text-gray-400 hover:bg-gray-100"
          >
            {subHabitsExpanded ? (
              <ChevronUpIcon className="h-4 w-4" />
            ) : (
              <ChevronDownIcon className="h-4 w-4" />
            )}
          </button>
        )}

        {/* Direct Open App button */}
        {habit.deeplink_url && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              openDeeplink(habit.deeplink_url)
            }}
            title={`Open app (${formatDeeplinkDisplay(habit.deeplink_url)})`}
            aria-label={`Open ${formatDeeplinkDisplay(habit.deeplink_url)} app`}
            className="rounded-full p-2 text-indigo-600 hover:bg-indigo-50 active:bg-indigo-100"
          >
            <ArrowTopRightOnSquareIcon className="h-5 w-5" />
          </button>
        )}

        <div ref={menuRef} className="relative shrink-0">
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-label="Habit options"
            className="rounded-full p-2 text-gray-400 hover:bg-gray-100 active:bg-gray-200"
          >
            <EllipsisVerticalIcon className="h-5 w-5" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 z-10 mt-1 w-36 rounded-xl border border-gray-100 bg-white py-1 shadow-lg">
              <button
                type="button"
                onClick={async () => {
                  setMenuOpen(false)
                  await onTogglePinned(habit)
                }}
                className="w-full px-4 py-2 text-left text-gray-700 text-sm hover:bg-gray-50"
              >
                {habit.pinned ? 'Unpin' : 'Pin'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false)
                  onEdit(habit)
                }}
                className="w-full px-4 py-2 text-left text-gray-700 text-sm hover:bg-gray-50"
              >
                Edit
              </button>
              {!hasSubHabits && !isReadOnly && (
                <button
                  type="button"
                  onClick={async () => {
                    setMenuOpen(false)
                    await onSkip(habit)
                  }}
                  className="w-full px-4 py-2 text-left text-amber-600 text-sm hover:bg-amber-50"
                >
                  {habit.isSkippedThisPeriod ? 'Un-skip' : 'Skip'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Sub-habits list */}
      {hasSubHabits && subHabitsExpanded && (
        <div className="flex flex-col gap-1.5 border-gray-100 border-t px-4 pt-2 pb-3">
          {(habit.subHabits ?? []).map((subHabit) => (
            <SubHabitRow
              key={subHabit.id}
              subHabit={subHabit}
              parentId={habit.id}
              parentIsCompleted={habit.isCompletedThisPeriod}
              selectedDate={selectedDate}
              dayStartTime={dayStartTime}
              isReadOnlyDate={isReadOnly}
              isFutureDate={isReadOnly}
              onToggle={onSubHabitToggle ?? (async () => {})}
            />
          ))}
        </div>
      )}
    </div>
  )
}
